import DatePicker, { DateRangeInput } from 'components/inputs/DatePicker';
import LabelWrap from 'components/inputs/LabelWrap';
import DateSpanPresetsSelector from 'pages/GlobalSearch/searchBar/DateSpanPresetsSelector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import AutoApprovalTable from './AutoApprovalTable';
import {
  AdvancedAssignmentFilterOption,
  ApprovalFilterEnum,
  AssignmentStatusEnum,
  AssignmentWithAutoApprovalCondition,
} from 'api/model';
import {
  useAssignmentGetAssignmentsWithAutoApprovalConditions,
  useAssignmentGetAutoApproveLastRunAt,
  useAssignmentRefreshAllAutoApproveChecksForFilter,
  useAssignmentRunAutoApproveRoutine,
} from 'api/assignment/assignment';
import toast from 'react-hot-toast';
import { Outlet } from 'react-router-dom';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import MAX_APPROVAL_PAGE_SIZE from './constants';
import Input from 'components/inputs/Input';
import TextButton from 'components/inputs/TextButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faSpinner, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import ApprovalFilterDropdown from './ApprovalFilterDropdown';
import { useSessionStorage } from 'usehooks-ts';
import Checkbox from 'components/inputs/Checkbox';

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  padding: 10px;
  gap: 20px;
  align-items: flex-end;
`;

const DatePickerWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;

const CheckStatusText = styled.span`
  font-size: 0.8em;
  color: #888;
  white-space: nowrap;
`;

const getInitialDateRange = (): [Date, Date] => {
  const now = new Date();
  const startOfYesterday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 1,
    0,
    0,
    0,
    0
  );
  const endOfYesterday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 1,
    23,
    59,
    59,
    999
  );

  return [startOfYesterday, endOfYesterday];
};

// const POLL_INTERVAL_MS = 60_000; // 60 seconds

const AssignmentApproval = () => {
  const [daterange, setDateRange] = useState<[Date | null, Date | null]>(
    getInitialDateRange()
  );
  const [skip, setSkip] = useState<number>(0);
  const [businessUnitIDs, setBusinessUnitIDs] = useSessionStorage<number[]>(
    'approveassignmentslist-businessUnitIDs',
    []
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalUserCount, setTotalUserCount] = useState<number>(0);
  const [approvalStatusAssignments, setApprovalStatusAssignments] = useState<
    AssignmentWithAutoApprovalCondition[]
  >([]);
  const [showOnlyFullDayComplete, setShowOnlyFullDayComplete] =
    useSessionStorage<boolean>(
      'approveassignmentslist-showOnlyFullDayComplete',
      false
    );
  const [excludeApprovedDays, setExcludeApprovedDays] = useState<boolean>(true);
  const [groupByUser, setGroupByUser] = useSessionStorage<boolean>(
    'approveassignmentslist-groupByUser',
    true
  );
  const [approvalFilter, setApprovalFilter] =
    useSessionStorage<ApprovalFilterEnum>(
      'approveassignmentslist-approvalFilter',
      ApprovalFilterEnum.All
    );
  const [onlyAutoApproveUsers, setOnlyAutoApproveUsers] =
    useSessionStorage<boolean>(
      'approveassignmentslist-onlyAutoApproveUsers',
      false
    );
  // const [showAssignmentsToApprove, setShowAssignmentsToApprove] =
  //   useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');

  const getAssignmentsCall =
    useAssignmentGetAssignmentsWithAutoApprovalConditions();
  const refreshAllChecksCall =
    useAssignmentRefreshAllAutoApproveChecksForFilter();
  const runRoutineCall = useAssignmentRunAutoApproveRoutine();
  const lastRunCall = useAssignmentGetAutoApproveLastRunAt();
  const getAssignments = async () => {
    if (!daterange[0] || !daterange[1]) {
      return [];
    }

    const res = await getAssignmentsCall.mutateAsync({
      data: {
        startDate: daterange[0],
        endDate: daterange[1],
        businessUnits: businessUnitIDs,
        assignmentStatuses: [
          AssignmentStatusEnum.Complete,
          AssignmentStatusEnum.Canceled,
        ],
        advancedSearchOptions: [
          AdvancedAssignmentFilterOption.IncludePlaceholderAndTransportAssignments,
        ],
        searchStr: searchString,
        size: MAX_APPROVAL_PAGE_SIZE,
        skip,
        showOnlyFullDayComplete,
        excludeApprovedDays,
        approvalFilter,
        onlyAutoApproveUsers,
      },
    });
    if (!res || !res.assignments) {
      return [];
    }

    // setPage(res.skip / MAX_APPROVAL_PAGE_SIZE);
    setTotalCount(res.totalCount);
    setTotalUserCount(res.totalUserCount);
    return res.assignments;
  };

  const getAssignmentsRef = useRef(getAssignments);
  getAssignmentsRef.current = getAssignments;

  useEffect(() => {
    const [start, end] = getInitialDateRange();
    setDateRange([start, end]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      getAssignments().then(setApprovalStatusAssignments);
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    daterange,
    skip,
    searchString,
    businessUnitIDs,
    showOnlyFullDayComplete,
    excludeApprovedDays,
    approvalFilter,
    onlyAutoApproveUsers,
  ]);

  useEffect(() => {
    // Reset skip when date range, search string or filters change
    setSkip(0);
  }, [daterange, searchString, approvalFilter, onlyAutoApproveUsers]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // Runs the auto-approve routine on demand — same behaviour as the background AutoApproveFunction
  // (grace period respected), so only assignments past their grace window get approved.
  const runAutoApprove = useCallback(async () => {
    setIsRunning(true);
    try {
      const approvedCount = await runRoutineCall.mutateAsync();
      toast.success(
        approvedCount > 0
          ? `Autogodkände ${approvedCount} uppdrag.`
          : 'Inga uppdrag var redo att autogodkännas.'
      );
      const updated = await getAssignmentsRef.current();
      setApprovalStatusAssignments(updated);
      lastRunCall.refetch();
    } catch {
      toast.error('Det gick inte att köra autogodkänningen.');
    } finally {
      setIsRunning(false);
    }
  }, [runRoutineCall, lastRunCall]);

  const lastRunSummary = useMemo(() => {
    const lastRun = lastRunCall.data;
    if (!lastRun) return 'Senaste autogodkänning: aldrig körd';
    const d = new Date(lastRun);
    return `Senaste autogodkänning: ${d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`;
  }, [lastRunCall.data]);

  // Refreshes checks for EVERY assignment matching the current filter — across all pages — not
  // just the page in view. The backend resolves the full set with the same filter the list uses
  // (pagination disabled) so a single click covers the whole list instead of having to be re-run
  // per page.
  const refreshAllChecks = useCallback(async () => {
    if (!daterange[0] || !daterange[1]) return;
    setIsRefreshing(true);
    try {
      await refreshAllChecksCall.mutateAsync({
        data: {
          startDate: daterange[0],
          endDate: daterange[1],
          businessUnits: businessUnitIDs,
          assignmentStatuses: [
            AssignmentStatusEnum.Complete,
            AssignmentStatusEnum.Canceled,
          ],
          advancedSearchOptions: [
            AdvancedAssignmentFilterOption.IncludePlaceholderAndTransportAssignments,
          ],
          searchStr: searchString,
          showOnlyFullDayComplete,
          excludeApprovedDays,
          approvalFilter,
          onlyAutoApproveUsers,
        },
      });
      const updated = await getAssignmentsRef.current();
      setApprovalStatusAssignments(updated);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    daterange,
    businessUnitIDs,
    searchString,
    showOnlyFullDayComplete,
    excludeApprovedDays,
    approvalFilter,
    onlyAutoApproveUsers,
    refreshAllChecksCall,
  ]);

  const checkStatusSummary = useMemo(() => {
    if (approvalStatusAssignments.length === 0) return null;
    const uncheckedCount = approvalStatusAssignments.filter(
      (a) => !a.checkedAt
    ).length;
    const checkedDates = approvalStatusAssignments
      .filter((a) => a.checkedAt)
      .map((a) => new Date(a.checkedAt!).getTime());
    const oldestCheck =
      checkedDates.length > 0 ? new Date(Math.min(...checkedDates)) : null;

    const parts: string[] = [];
    if (uncheckedCount > 0) {
      parts.push(`${uncheckedCount} ej kontrollerade`);
    }
    if (oldestCheck) {
      parts.push(
        `Äldsta kontroll: ${oldestCheck.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} ${oldestCheck.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`
      );
    }
    return parts.join(' \u00b7 ');
  }, [approvalStatusAssignments]);

  // No need for client-side filtering anymore since we moved showOnlyFullDayComplete to backend
  const filteredApprovalStatusAssignments = approvalStatusAssignments;

  return (
    <>
      <Actions>
        <DatePickerWrap>
          <LabelWrap label="Datumspann">
            <DatePicker
              calendarStartDay={1}
              customInput={<DateRangeInput daterange={daterange} />}
              dateFormat="yyyy-MM-dd"
              endDate={daterange[1]}
              locale="sv"
              onChange={setDateRange}
              populateHolidays
              selectsRange
              startDate={daterange[0]}
            />
          </LabelWrap>

          <DateSpanPresetsSelector
            dateRange={daterange}
            onPresetSelected={setDateRange}
            showYesterday
          />
        </DatePickerWrap>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <Input
            placeholder="Sök uppdrag..."
            value={searchString}
            onChange={(e) => {
              setSearchString(e.target.value);
            }}
            style={{ width: '300px' }}
            // autoFocus
          />
          <ApprovalFilterDropdown
            businessUnitIDs={businessUnitIDs}
            setBusinessUnitIDs={setBusinessUnitIDs}
            approvalFilter={approvalFilter}
            setApprovalFilter={setApprovalFilter}
            onlyAutoApproveUsers={onlyAutoApproveUsers}
            setOnlyAutoApproveUsers={setOnlyAutoApproveUsers}
            showOnlyFullDayComplete={showOnlyFullDayComplete}
            setShowOnlyFullDayComplete={setShowOnlyFullDayComplete}
            excludeApprovedDays={excludeApprovedDays}
            setExcludeApprovedDays={setExcludeApprovedDays}
          />

          <Checkbox
            checked={groupByUser}
            label="Gruppera uppdrag per fälttestare och datum"
            onChange={() => {
              setGroupByUser(!groupByUser);
            }}
          >
            Gruppera uppdrag per fälttestare och datum
          </Checkbox>
          <TextButton
            onClick={refreshAllChecks}
            disabled={isRefreshing || approvalStatusAssignments.length === 0}
            title="Kör om godkännbarhetskontroller för alla uppdrag i filtret (samtliga sidor)"
          >
            {isRefreshing ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSyncAlt} />
            )}
            {' Uppdatera kontroller'}
          </TextButton>
          {checkStatusSummary && (
            <CheckStatusText>{checkStatusSummary}</CheckStatusText>
          )}
          <TextButton
            onClick={runAutoApprove}
            disabled={isRunning}
            title="Kör autogodkänn-snurran nu — godkänner alla godkännbara uppdrag vars grace-period passerat"
          >
            {isRunning ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faRobot} />
            )}
            {' Kör autogodkänning'}
          </TextButton>
          <CheckStatusText>{lastRunSummary}</CheckStatusText>
        </div>
      </Actions>

      {getAssignmentsCall.isLoading ? (
        <LoadingSpinner>
          <p>Laddar uppdrag...</p>
        </LoadingSpinner>
      ) : (
        <AutoApprovalTable
          autoApprovalResponse={{
            assignments: filteredApprovalStatusAssignments,
            size: MAX_APPROVAL_PAGE_SIZE,
            skip,
            totalCount,
            totalUserCount,
          }}
          onUpdate={(approvedAssignmentIds) => {
            if (approvedAssignmentIds && approvedAssignmentIds.length > 0) {
              // Optimized update: mark specific assignments as approved without refetching
              setApprovalStatusAssignments((prevAssignments) =>
                prevAssignments.map((assignment) => {
                  if (
                    approvedAssignmentIds.includes(
                      assignment.assignment.assignment.assignmentID
                    )
                  ) {
                    return {
                      ...assignment,
                      assignment: {
                        ...assignment.assignment,
                        assignment: {
                          ...assignment.assignment.assignment,
                          assignmentStatusID: AssignmentStatusEnum.Approved,
                        },
                      },
                    };
                  }
                  return assignment;
                })
              );
            } else {
              // Fallback: full refresh
              getAssignments();
              getAssignmentsRef.current().then(setApprovalStatusAssignments);
            }
          }}
          onPageChange={async (newPage) => {
            setSkip(newPage * MAX_APPROVAL_PAGE_SIZE);
          }}
          onFilterByUser={(userName) => {
            setSearchString(userName);
          }}
          groupByUser={groupByUser}
        />
      )}

      <Outlet />
    </>
  );
};

export default AssignmentApproval;
